// Offscreen 3D renderer for player head avatars. Builds a tiny WebGPU scene
// per head (sphere + eyes + optional hat), renders once, and returns the
// canvas. Caller is responsible for holding a reference to the canvas (or a
// data URL derived from it) so the result sticks around.
//
// Patterned after `renderHatIcon` in HomeScreen.ts — same two-phase WebGPU
// init pattern, same transparent clear color, and we keep the renderer
// alive on the canvas so AA survives a later toDataURL() call on drivers
// that drop antialiasing for composited WebGPU canvases.

import * as THREE from 'three';
import { PMREMGenerator, WebGPURenderer } from 'three/webgpu';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { CONFIG } from '../config';
import { paletteFromBase } from './palette';
import { ToonMaterial } from '../render/materials/ToonMaterial';
import { OutlineMaterial } from '../render/materials/OutlineMaterial';
import { loadHatModel, type HatId } from '../render/HatModels';

export interface HeadIconOptions {
  color: string;
  hatId: HatId | null;
  size?: number; // rendered pixel square (default 64)
}

const HEAD_RADIUS = 0.34;

export async function renderHeadIcon(opts: HeadIconOptions): Promise<HTMLCanvasElement> {
  const size = opts.size ?? 96;
  const canvas = document.createElement('canvas');
  canvas.width = size; canvas.height = size;
  const renderer = new WebGPURenderer({ canvas, antialias: true, alpha: true, powerPreference: 'high-performance' });
  await renderer.init();
  renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
  renderer.setSize(size, size, false);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.setClearColor(0x000000, 0);

  const scene = new THREE.Scene();
  const pmrem = new PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
  pmrem.dispose();
  scene.add(new THREE.AmbientLight(0xffffff, 0.7));
  const dir = new THREE.DirectionalLight(0xfff2d8, 1.9);
  dir.position.set(-1.5, 2.5, 2);
  scene.add(dir);

  // Toon-shaded head sphere with matching outline — same look as the
  // in-world characters. Palette is derived from the raw base color.
  const palette = paletteFromBase(opts.color);
  const headGroup = new THREE.Group();
  const mat = new ToonMaterial({
    baseColor: palette.base,
    shadowColor: palette.shadow,
    midColor: palette.mid,
    highColor: palette.high,
    rimColor: palette.rim,
    shadowLift: 1.0
  });
  // Match the in-world toon light direction so the flat-shaded plateaus land
  // on the front of the head rather than baking a rim band across the face.
  mat.setLightDir(new THREE.Vector3(1.5, -2.5, -2).normalize());
  const outlineMat = new OutlineMaterial(2.6, palette.outline);
  const headGeo = new THREE.SphereGeometry(HEAD_RADIUS, 28, 22);
  const head = new THREE.Mesh(headGeo, mat);
  const outline = new THREE.Mesh(
    new THREE.SphereGeometry(HEAD_RADIUS, 20, 16),
    outlineMat
  );
  headGroup.add(outline, head);

  const eyeGeo = new THREE.SphereGeometry(HEAD_RADIUS * 0.14, 12, 10);
  const eyeMat = new THREE.MeshBasicMaterial({ color: 0x0a0a12 });
  for (const s of [-1, +1]) {
    const eye = new THREE.Mesh(eyeGeo, eyeMat);
    eye.position.set(s * HEAD_RADIUS * 0.32, HEAD_RADIUS * 0.08, HEAD_RADIUS * 0.88);
    head.add(eye);
  }
  scene.add(headGroup);

  if (opts.hatId) {
    try {
      const template = await loadHatModel(opts.hatId);
      const hat = template.clone(true);
      // `template` is pre-neutralised by `loadHatModel` (see HatModels.ts).
      // `clone(true)` shares materials by ref, so the neutralisation carries.
      const hatCfg = CONFIG.hats[opts.hatId];
      // Hat offset is relative to the head mesh's centre — same convention
      // as CharacterView in-game (CharacterView.ts:253-254). The previous
      // `HEAD_RADIUS + offsetY` here lifted hats an extra 0.34 units above
      // the head, which the prior tight camera zoom hid by clipping the
      // gap. Match the in-game math directly.
      hat.position.set(hatCfg.offsetX, hatCfg.offsetY, hatCfg.offsetZ);
      hat.rotation.set(
        hatCfg.pitchDeg * Math.PI / 180,
        hatCfg.yawDeg * Math.PI / 180,
        hatCfg.rollDeg * Math.PI / 180
      );
      hat.scale.setScalar(hatCfg.scale);
      headGroup.add(hat);
    } catch (err) {
      console.warn('[HeadIcon] hat load failed:', err);
    }
  }

  // Frame the camera: head + a bit of headroom for any hat. The original
  // 4.2r distance was way too tight (eyes-and-top-of-head only); the
  // first-pass 12.6r fix overshot in the other direction (head dwarfed in
  // its own frame). Halve the latter for a 2x zoom-in: head fills most of
  // the frame, hat fits, with a thin margin for the level badge below.
  const camera = new THREE.PerspectiveCamera(22, 1, 0.01, 100);
  camera.position.set(0, HEAD_RADIUS * 0.4, HEAD_RADIUS * 6.3);
  camera.lookAt(0, HEAD_RADIUS * 0.5, 0);

  renderer.render(scene, camera);

  // Hold the renderer alive (see HomeScreen.renderHatIcon comment).
  (canvas as unknown as { _owner: { renderer: WebGPURenderer } })._owner = { renderer };
  return canvas;
}

/** Convenience: render the icon and return a data URL. The URL caches on
 *  first access so repeated calls to .toDataURL() don't re-serialise.
 *  Callers typically use this once, then feed the result into an <img>
 *  src. */
export async function renderHeadIconDataUrl(opts: HeadIconOptions): Promise<string> {
  const canvas = await renderHeadIcon(opts);
  return canvas.toDataURL('image/png');
}
